import {
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form940Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="File Form 940 Easily: 2025 Instructions & Due Dates"
          description="Learn how to file Form 940 to report Federal Unemployment Taxes (FUTA) for 2025. This guide includes step-by-step instructions and important deadlines."
          keywords=""
          canonical="resources/employment-tax/form-940-instructions/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 940</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />

          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box>
                    <Box
                      itemScope
                      itemType="https://schema.org/Article"
                      sx={{
                        p: 3,
                        px: 3,
                        backgroundColor: '#f0f5f8',
                        borderLeft: '5px solid #01456c',
                        mb: 4,
                        borderRadius: 3,
                        position: 'relative',
                      }}
                    >
                      <link
                        itemProp="mainEntityOfPage"
                        href="https://www.taxzerone.com/resources/employment-tax/form-940-instructions/"
                      />

                      {/* Article requires an image */}
                      <div
                        itemProp="image"
                        itemScope
                        itemType="https://schema.org/ImageObject"
                        style={{ display: 'none' }}
                      >
                        <link
                          itemProp="url"
                          href="https://www.taxzerone.com/assets/images/logo.webp"
                        />
                        <data itemProp="width" value="380" />
                        <data itemProp="height" value="124" />
                      </div>

                      <Box
                        sx={{
                          display: 'grid',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          textAlign: 'left',
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2">
                          Last updated:{' '}
                          <time
                            itemProp="datePublished"
                            dateTime="2026-06-15T09:00:00-05:00"
                          >
                            June 15, 2026
                          </time>
                          <time
                            itemProp="dateModified"
                            dateTime="2026-06-15T09:00:00-05:00"
                            style={{ display: 'none' }}
                          >
                            June 15, 2026
                          </time>
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                          itemProp="author"
                          itemScope
                          itemType="https://schema.org/Person"
                        >
                          -by{' '}
                          <span itemProp="name">
                            <a
                              itemProp="url"
                              href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                              style={{
                                color: 'inherit',
                                textDecoration: 'none',
                              }}
                            >
                              Nikhil Chowdary
                            </a>
                          </span>{' '}
                          | TaxZerone
                        </Typography>
                      </Box>

                      <Typography
                        variant="h4"
                        itemProp="headline"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          fontSize: { md: 20, sm: 18, xs: 16 },

                          color: '#01456c',
                          fontStyle: 'italic',
                        }}
                      >
                        Form 940 at a Glance
                      </Typography>

                      <Box
                        component="ul"
                        itemProp="articleBody"
                        sx={{
                          mb: 0,
                          pl: 0,
                          listStyle: 'none',

                          '& li': {
                            mb: 1.6,
                            position: 'relative',
                            pl: 3,
                          },

                          '& li::before': {
                            content: '"★"',
                            position: 'absolute',
                            left: 0,
                            top: '-2px',
                            color: '#01456c',
                            fontSize: '18px',
                          },
                        }}
                      >
                        <li>
                          <span>
                            {' '}
                            Form 940,{' '}
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              Employer's Annual Federal Unemployment (FUTA) Tax
                              Return
                            </span>
                            , is used by employers to report and pay their FUTA
                            tax with the IRS. It is{' '}
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              paid by employers
                            </span>{' '}
                            and is not withheld from employee wages.
                          </span>
                        </li>
                        <li>
                          <span>
                            <Link
                              href="/resources/employment-tax/irs-form-940-due-dates/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Due Date
                            </Link>{' '}
                            : Form 940 is filed{' '}
                            <span style={{ fontWeight: 600 }}>
                              annually
                            </span>{' '}
                            and is due by{' '}
                            <span style={{ fontWeight: 600 }}>January 31</span>.
                          </span>
                        </li>
                        <li>
                          <span>
                            <span style={{ fontWeight: 600 }}> Penalties:</span>{' '}
                            Failure to file or pay Form 940 on time may result
                            in
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              IRS penalties, interest, and late payment charges.
                            </span>
                          </span>
                        </li>
                      </Box>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                      Form 940 is an IRS tax form used to report Federal
                      Unemployment Taxes(FUTA). Employers who pay federal
                      unemployment taxes must file this form on an annual basis.
                      These taxes are specific to employers; no unemployment
                      taxes are deducted from their employees&apos; wages.
                      Here&apos;s a summary of the Form 940 instructions for the
                      2025 tax year.
                    </Typography>
                  </Box>
                  <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  />
                </Stack>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What&apos;s New in the IRS Form 940?
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Aggregate return filers:
                    </span>{' '}
                    Use the new “Aggregate Return Filers Only” section on Form
                    940 to indicate whether you’re filing as a section 3504
                    agent, a certified professional employer organization
                    (CPEO), or a third-party filer.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Direct deposit of Form 940 refund:{' '}
                    </span>{' '}
                    Following{' '}
                    <Link
                      href="https://www.irs.gov/eo14247"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Executive Order 14247
                    </Link>{' '}
                    (March 25, 2025), the IRS now issues Form 940 refunds by
                    direct deposit for faster and more secure payments. You can
                    also apply any overpayment to your next return (see line
                    15b).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Electronic Payments Required:
                    </span>{' '}
                    EO 14247 also mandates electronic payment of any balance due
                    on Form 940. See line 14 instructions for details.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Electronic Return Transcripts:
                    </span>{' '}
                    Form 940{' '}
                    <Link
                      href="https://www.irs.gov/businesstranscript"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      transcripts
                    </Link>{' '}
                    for tax years 2023 and later are now available through your{' '}
                    <span style={{ fontWeight: 600 }}>
                      IRS Business Tax Account
                    </span>{' '}
                    at{' '}
                    <Link
                      href="https://www.irs.gov/BusinessAccount"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      IRS.gov/BusinessAccount
                    </Link>
                    .{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Credit reduction state:
                    </span>{' '}
                    For 2025, if you paid wages in a credit reduction state,
                    your FUTA credit will be reduced based on that state’s
                    credit reduction rate. Use Schedule A (Form 940) to
                    calculate the reduction.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Step- by- Step instructions for Form 940:
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f940-step-by-step-img.webp" />
                  <source srcSet="/assets/images/f940-step-by-step-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f940-step-by-step-img.png"
                    loading="lazy"
                    alt="Form 940: Employer tax form with EIN, business info, return type, and filer checkboxes "
                    width={748}
                    height={265}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the EIN, Name of your business, trade name (if any),
                    complete address such as number, street, city, state and ZIP
                    code.{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Types of Return —
                    </span>{' '}
                    Check all that apply:{' '}
                  </li>
                  <ol type="a">
                    <li>
                      <span style={{ fontWeight: 600 }}> Amended –</span> Use if
                      you’re correcting a previously filed return.
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Successor employer –{' '}
                      </span>
                      Use if you’ve taken over another business
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        No payments to employees in 2025 –{' '}
                      </span>
                      Use if you had no employee wages during the year.
                    </li>
                    <li>
                      {' '}
                      <span style={{ fontWeight: 600 }}> Final – </span>Use if
                      your business has closed or you permanently stopped paying
                      wages.{' '}
                    </li>
                  </ol>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Aggregate Return Filers Only -
                    </span>{' '}
                    Choose any <span style={{ fontWeight: 600 }}>one box</span>{' '}
                    that applies:{' '}
                  </li>
                  <ul>
                    <li>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Section 3504 Agent —
                      </span>{' '}
                      Check this if you are an employer agent who is filing on
                      behalf of clients under section 3504.
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>
                        Certified Professional Employer Organization (CPEO) —
                      </span>{' '}
                      check this if a CPEO is filing this return on behalf of
                      the client.{' '}
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Other Third Party —
                      </span>{' '}
                      Check this box, if you are a third-party filer.{' '}
                    </li>
                    <li>
                      If you are not an aggregate filer, leave these boxes
                      blank.{' '}
                    </li>
                  </ul>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Part 1: Information about the Form 940 return
                </Typography>
                <picture>
                  <source srcSet="/assets/images/940-part-1.webp" />
                  <source srcSet="/assets/images/940-part-1.png" />
                  <Box
                    component="img"
                    src="/assets/images/940-part-1.png"
                    loading="lazy"
                    alt="Form 940 Part 1- Lines 1a to 2: section for employers to report state unemployment tax details, multi-state status, and credit reduction"
                    width={766}
                    height={112}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  In Part 1 of the 940 Form, you must provide some basic
                  information about your return. Leave any lines that do not
                  apply to your business blank.
                </Typography>
                <Box component="ul" sx={{ mb: 4 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 1a:</b> Indicate if you are an employer who has paid
                    unemployment taxes in only one state. Enter the abbreviation
                    for that state.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 1b:</b> Indicate whether you have paid unemployment
                    taxes in more than one state. This means your company is a
                    multi-state employer, and you must complete and attach Form
                    940 Schedule A.
                  </li>
                  <li>
                    <b>Line 2:</b> Indicate whether you paid wages in a
                    credit-reduction state.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Part 2: Determine your FUTA tax before adjustments
                </Typography>
                <picture>
                  <source srcSet="/assets/images/940-part-2.webp" />
                  <source srcSet="/assets/images/940-part-2.png" />
                  <Box
                    component="img"
                    src="/assets/images/940-part-2.png"
                    loading="lazy"
                    alt="Form 940 Part 2- Lines 3 to 8: FUTA tax calculation form showing employee payments, exemptions, and taxable wage computation lines"
                    width={766}
                    height={241}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  In Part 2 of Form 940, you will calculate your FUTA tax before
                  any adjustments. Again, skip any lines that do not pertain to
                  your business.
                </Typography>
                <Box component="ul" sx={{ mb: 4 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 3:</b> Enter the total payments made to all
                    employees, even if some are not subject to FUTA taxes.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 4:</b> List any payments that are exempt from FUTA
                    tax. For example, payments that do not meet the definition
                    of wages. Check each of the following that apply:
                    <Box
                      component="ol"
                      sx={{
                        mt: 1,
                        listStyleType: 'lower-alpha',
                        '& li::before': {
                          display: 'inline-block',
                          content: "'4'",
                          width: '2em',
                          marginLeft: '-1.8em',
                        },
                      }}
                    >
                      <li style={{ marginBottom: 8 }}>Fringe benefits</li>
                      <li style={{ marginBottom: 8 }}>
                        Group-term life insurance
                      </li>
                      <li style={{ marginBottom: 8 }}>Retirement/Pension</li>
                      <li style={{ marginBottom: 8 }}>Dependent care</li>
                      <li>Other</li>
                    </Box>
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 5:</b> Enter any payments made to an employee that
                    exceed $7,000. The FUTA wage base is $7,000, meaning that
                    any amount above this threshold is not subject to FUTA
                    taxes. Subtract any payments exempt from FUTA taxes from
                    this total.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 6:</b> Enter the subtotal. Add the amounts from
                    Lines 4 and 5, then record the total on Line 6.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 7:</b> Total taxable FUTA wages. To calculate this
                    amount, subtract the amount on Line 6 from the amount on
                    Line 3.
                  </li>
                  <li>
                    <b>Line 8:</b> FUTA tax before modifications. Multiply the
                    amount on Line 7 by 0.006 to calculate this amount.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Part 3: Determine your adjustments
                </Typography>
                <picture>
                  <source srcSet="/assets/images/940-part-3.webp" />
                  <source srcSet="/assets/images/940-part-3.png" />
                  <Box
                    component="img"
                    src="/assets/images/940-part-3.png"
                    loading="lazy"
                    alt="Form 940 Part 3- Lines 9 to 11: showing adjustment calculations for wages excluded from state unemployment tax."
                    width={766}
                    height={120}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  In Part 3, you will make your adjustments. Only fill out the
                  lines that apply to your business.
                </Typography>
                <Box component="ul" sx={{ mb: 4 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 9:</b> If all taxable FUTA wages were exempt from
                    state unemployment taxes, multiply the amount on Line 7 by
                    0.054 and enter the result here. Then proceed to Part 4.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 10:</b> If some of the taxable FUTA wages were
                    exempt from state unemployment taxes, complete this line
                    accordingly. Also, be sure to fill out Line 9 if you paid
                    any state unemployment taxes late.
                  </li>
                  <li>
                    <b>Line 11:</b> If a credit reduction applies, complete this
                    line by entering the total from Schedule A.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Part 4: Determine your FUTA tax and balance due or overpayment
                </Typography>
                <picture>
                  <source srcSet="/assets/images/940-part-4.webp" />
                  <source srcSet="/assets/images/940-part-4.png" />
                  <Box
                    component="img"
                    src="/assets/images/940-part-4.png"
                    loading="lazy"
                    alt="Form 940 Part 4 - Lines 12 to 15e: showing total tax after adjustments, deposits made, and balance due or overpayment options including refund or credit."
                    width={760}
                    height={229}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  In Part 4, you will be determining your FUTA tax balance due
                  and if you overpaid.
                </Typography>
                <Box component="ul" sx={{ mb: 4 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 12:</b> This is the total of Lines 8, 9, 10, and 11.
                    If the amount on Line 9 is more than zero, then Lines 10 and
                    11 must be zero.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 13:</b> Enter your FUTA deposits for the year here,
                    including any overpayments made in the previous year.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 14:</b> Calculate the balance due. If the amount on
                    Line 13 is less than the amount on Line 12, enter the
                    difference here.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 15a:</b> If Line 13 is greater than Line 12, enter
                    the difference on line 15a.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 15b:</b> If you have an overpayment, select whether
                    you want the amount{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      applied to your next return
                    </span>{' '}
                    or as a <span style={{ fontWeight: 600 }}>refund</span>.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 15c:</b> Enter the routing number
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 15d:</b> Check the account type – checking or
                    savings
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 15e:</b> Enter the account number savings
                  </li>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Part 5: Report your FUTA tax liability by quarter only if line
                  12 is more than $500
                </Typography>
                <picture>
                  <source srcSet="/assets/images/940-part-5.webp" />
                  <source srcSet="/assets/images/940-part-5.png" />
                  <Box
                    component="img"
                    src="/assets/images/940-part-5.png"
                    loading="lazy"
                    alt="Form 940 Part 5 - Lines 16 to 17: Quarterly liability fields for employers reporting over $500 in annual tax."
                    width={766}
                    height={225}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  You only need to complete Part 5 of Form 940 if the amount you
                  entered on Line 12 is $500 or more; otherwise, skip to Part 6.
                </Typography>
                <Box component="ul">
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 16:</b> Enter your FUTA liabilities for each
                    quarter.
                    <Box
                      component="ol"
                      sx={{
                        mt: 1,
                        listStyleType: 'lower-alpha',
                        '& li::before': {
                          display: 'inline-block',
                          content: "'16'",
                          width: '2.1em',
                          marginLeft: '-2.1em',
                        },
                      }}
                    >
                      <li style={{ marginBottom: 8 }}>(Q1)</li>
                      <li style={{ marginBottom: 8 }}>(Q2)</li>
                      <li style={{ marginBottom: 8 }}>(Q3)</li>
                      <li style={{ marginBottom: 8 }}>(Q4)</li>
                    </Box>
                  </li>

                  <li>
                    <b>Line 17:</b> Calculate the total FUTA liability for the
                    year by adding the amounts from each quarter.
                  </li>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 4,
                    lineHeight: 1.5,
                  }}
                >
                  Use these Form 940 instructions to easily{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-940/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 940
                  </Link>
                  .
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Part 6: Third-party designee information
                </Typography>

                <picture>
                  <source srcSet="/assets/images/940-part-6.webp" />
                  <source srcSet="/assets/images/940-part-6.png" />
                  <Box
                    component="img"
                    src="/assets/images/940-part-6.png"
                    loading="lazy"
                    alt="Form 940 Part 6: Checkboxes to authorize third-party designee, including name, phone, and 5-digit PIN."
                    width={766}
                    height={144}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you want to allow an employee/tax preparer/another person
                  to discuss your return with the IRS, check{' '}
                  <span style={{ fontWeight: 600 }}>“Yes”</span> and enter the
                  designee’s name, phone number, and a 5-digit PIN. Otherwise,
                  check <span style={{ fontWeight: 600 }}>“No”</span>.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 4,
                  }}
                >
                  What is the mailing address for paper filing of Form 940?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you file a paper return, the filing address depends on
                  whether you include a payment with Form 940. Mail your return
                  to the address listed for your location in the table below.
                  Please note that the IRS encourages filing Form 940
                  electronically.
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 200 }}>
                            If your location is...
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 200 }}>
                            Without a payment...
                          </TableCell>
                          <TableCell sx={{ color: '#444', width: 200 }}>
                            With a payment...
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Connecticut, Delaware, District of Columbia,
                            Georgia, Illinois, Indiana, Kentucky, Maine,
                            Maryland, Massachusetts, Michigan, New Hampshire,
                            New Jersey, New York, North Carolina, Ohio,
                            Pennsylvania, Rhode Island, South Carolina,
                            Tennessee, Vermont, Virginia, West Virginia,
                            Wisconsin
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}>
                              Department of the Treasury
                            </span>
                            <br />
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            Kansas City,
                            <br />
                            MO 64999-0046
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 932000
                            <br />
                            Louisville,
                            <br />
                            KY 40293-2000
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Alabama, Alaska, Arizona, Arkansas, California,
                            Colorado, Florida, Hawaii, Idaho, Iowa, Kansas,
                            Louisiana, Minnesota, Mississippi, Missouri,
                            Montana, Nebraska, Nevada, New Mexico, North Dakota,
                            Oklahoma, Oregon, South Dakota, Texas, Utah,
                            Washington, Wyoming
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}>
                              Department of the Treasury
                            </span>
                            <br />
                            Internal Revenue Service
                            <br />
                            Ogden, UT 84201-0046
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 932000
                            <br />
                            Louisville, KY 40293-2000
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            Puerto Rico, U.S. Virgin Islands
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 409101 Ogden,
                            <br />
                            UT 84409
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 932000
                            <br />
                            Louisville,
                            <br />
                            KY 40293-2000
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            If the location of your legal residence, principal
                            place of business, office, or agency is not listed
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 409101 Ogden,
                            <br />
                            UT 84409
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 932000 Louisville,
                            <br />
                            KY 40293-2000
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}> EXCEPTION</span>{' '}
                            for tax-exempt organizations; federal, state, and
                            local governments; and Indian tribal governments,
                            regardless of your location
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}>
                              Department of the Treasury
                            </span>
                            <br />
                            Internal Revenue Service
                            <br />
                            Ogden,
                            <br />
                            UT 84201-0046
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 932000
                            <br />
                            Louisville,
                            <br />
                            KY 40293-2000
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to get started with Form 940 e-filing?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              E-file your Form 940 for the 2025 tax year easily with TaxZerone
              for <span style={{ fontWeight: 600 }}>just $6.99 per return</span>
              .
              <br />
              Click the button below and start your filing process with
              TaxZerone.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=940instructions`}
              >
                E-file Now!
              </Button>
              <Typography variant="body1" sx={{ mt: 1 }}>
                Takes 3 simple steps
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form940Instructions;
